<script setup lang="ts">
import { tv } from 'tailwind-variants'

definePageMeta({
  name: 'product',
  middleware: [
    // If the slug matches a level, we redirect to the product catalogue with the correct level filter
    // E.g. /produktkatalog/1-7 -> /produktkatalog?level=1-7
    (to) => {
      const slug = (to as ReturnType<typeof useRoute<'product'>>).params.slug.toLowerCase()
      const level = LEVELS.find((it) => it.toLowerCase() === slug)
      if (level) {
        return navigateTo({
          name: 'products',
          query: {
            level,
          },
        }, {
          redirectCode: 301,
        })
      }
    },
    'migrate-product-formats',
  ],
})

const route = useRoute<'product'>()

const slug = computed(() => route.params.slug)

const { data: product, suspense } = useProduct(slug)
const { style } = useColorTheme(computed(() => product.value?.theme as ColorTheme))

await suspense()

if (!product.value) {
  const catalogue = useProducts()

  // FIXME: Not sure if this is allowed, as we have already suspended requests (L16).
  await catalogue.suspense()

  const fromCatalogue = catalogue.data.value?.products?.find((it) => it.slug.short === slug.value || it.slug.custom === slug.value)

  if (fromCatalogue) {
    await navigateTo(`/produktkatalog/${fromCatalogue.slug.short}`, {
      redirectCode: 301,
    })
  } else {
    throw createError({
      status: 404,
      message: 'Produktet finnes ikke',
      fatal: true,
    })
  }
}

const bodyClass = tv({
  base: '[--curtain:var(--background)] [--text:white]',
  variants: {
    theme: {
      'green-seagreen': 'bg-seagreen-40',
      'dark-red-pink': 'bg-coral-60',
      'blue-pink': 'bg-blue-60',
      'green-red': 'bg-green-50',
      'green': 'bg-(--background) [--variant-background:177,219,208]',
      'purple': 'bg-(--background) [--variant-background:209,204,231]',
      'blue': 'bg-(--background) [--variant-background:204,227,236]',
    },
    template: {
      standard: '[--color:white]',
      standard2: '[--color:white]',
      pluss: '',
      grunnskole: '[--color:white]',
    },
  },
  compoundVariants: [
    {
      theme: 'green',
      template: ['standard', 'standard2', 'grunnskole'],
      class: [
        '[--background:var(--color-green-30)]',
        '[--foreground:var(--color-green-50)]',
        '[--accent-color:var(--color-green-20)]',
        '[--filter-background:#f5f9f8]',
        '[--filter-color:var(--color-green-50)]',
      ],
    },
    {
      theme: 'green',
      template: 'pluss',
      class: [
        '[--background:var(--color-green-10)]',
        '[--card-background:var(--color-green-5)]',
        '[--color:#015f40]',
      ],
    },
    {
      theme: 'blue',
      template: ['standard', 'grunnskole'],
      class: [
        '[--background:#01739f]',
        '[--foreground:#14475f]',
        '[--accent-color:var(--color-blue-30)]',
        '[--filter-background:#f1f8fb]',
        '[--filter-color:#14475f]',
      ],
    },
    {
      theme: 'blue',
      template: 'pluss',
      class: [
        '[--background:var(--color-blue-20)]',
        '[--card-background:var(--color-blue-10)]',
        '[--color:var(--color-blue-40)]',
      ],
    },
    {
      theme: 'blue',
      template: 'standard2',
      class: [
        '[--background:#01739f]',
        '[--foreground:#14475f]',
        '[--card-color:var(--color-orange-5)]',
        '[--accent-color:var(--color-orange-pop)]',
        '[--filter-background:var(--card-color)]',
        '[--filter-color:var(--foreground)]',
      ],
    },
    {
      theme: 'purple',
      template: ['standard', 'standard2', 'grunnskole'],
      class: [
        '[--background:var(--color-purple-40)]',
        '[--foreground:#3e264d]',
        '[--accent-color:#9571a6]',
        '[--filter-background:#f3f2f9]',
        '[--filter-color:var(--color-violet-50)]',
      ],
    },
    {
      theme: 'purple',
      template: 'pluss',
      class: [
        '[--background:#ccd9ef]',
        '[--card-background:#eaf0f9]',
        '[--color:var(--color-blue-50)]',
      ],
    },
  ],
})

const title = computed(() => product.value?.title ?? product.value?.name)

useSeoMeta({
  title,
  ogTitle: title,
  twitterTitle: title,
})

useHead({
  bodyAttrs: {
    class: computed(() => bodyClass({
      theme: product.value?.theme,
      template: product.value && 'template' in product.value ? product.value.template : undefined,
    })),
  },
})
</script>

<template>
  <div :style class="[--filter-background:var(--theme-5)] [--filter-color:var(--theme-60)] [--type-background:var(--theme-20)]">
    <LayoutBreadCrumbs
      :background="product?.type === 'single-product' && product.template === 'pluss'"
      :text-conversions="{
        [slug]: product?.title || product?.name,
      }"
      :class="[
        product?.type === 'single-product' && product.template === 'standard2'
          ? 'bg-(--theme-50)'
          : 'bg-(--background)',
      ]"
    />
    <UiPacket v-if="product?.type === 'packet'" :packet="product" />
    <UiSingleProduct
      v-else-if="product?.type === 'single-product'"
      :product="product"
    />
  </div>
</template>
